import { useEffect, useRef, useState, type ChangeEvent } from "react";
import type { MediaAsset } from "@led-signage/shared/management-api";
import { api, usingFixtures } from "./api/index.js";
import { newIdempotencyKey } from "./api/client.js";
import { describeFailure, type ActionFailure } from "./api/errors.js";
import { AuthenticationRecoveryButton } from "./AuthenticationRecoveryButton.js";
import { byteLabel } from "./present.js";
import { putPresignedUpload, sha256Blob } from "./media-upload.js";

type UploadStage =
  | { kind: "ready" }
  | { kind: "hashing"; processed: number; total: number }
  | { kind: "requesting" }
  | { kind: "uploading"; processed: number; total: number }
  | { kind: "completing" }
  | { kind: "success"; media: MediaAsset }
  | { kind: "cancelled" }
  | { kind: "failed"; failure: ActionFailure };

interface PendingFile {
  file: File;
  sha256: string | null;
  idempotencyKey: string;
}

function isRunning(stage: UploadStage): boolean {
  return ["hashing", "requesting", "uploading", "completing"].includes(stage.kind);
}

function percent(processed: number, total: number): number {
  return total === 0 ? 0 : Math.min(100, Math.round((processed / total) * 100));
}

function stageLabel(stage: UploadStage): string | null {
  switch (stage.kind) {
    case "hashing":
      return `完全性を確認中… ${String(percent(stage.processed, stage.total))}%`;
    case "requesting":
      return "安全な送信先を準備中…";
    case "uploading":
      return `動画を送信中… ${String(percent(stage.processed, stage.total))}%`;
    case "completing":
      return "送信結果を確認し、形式検査を開始中…";
    default:
      return null;
  }
}

export function MediaUploadDialog({
  onClose,
  onUploaded,
}: {
  onClose: () => void;
  onUploaded: (media: MediaAsset) => void;
}) {
  const [pending, setPending] = useState<PendingFile | null>(null);
  const [stage, setStage] = useState<UploadStage>({ kind: "ready" });
  const controller = useRef<AbortController | null>(null);
  const closeButton = useRef<HTMLButtonElement | null>(null);
  const running = isRunning(stage);

  useEffect(() => {
    closeButton.current?.focus();
  }, []);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !running) onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => { window.removeEventListener("keydown", closeOnEscape); };
  }, [onClose, running]);

  const choose = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    if (file === null) return;
    if (!file.name.toLowerCase().endsWith(".mp4") ||
      (file.type !== "" && file.type !== "video/mp4")) {
      setPending(null);
      setStage({
        kind: "failed",
        failure: {
          message: "MP4形式の動画を選択してください。",
          code: "INVALID_FILE_TYPE",
          reloadHelps: false,
          fullPageReloadRequired: false,
        },
      });
      return;
    }
    if (!Number.isSafeInteger(file.size) || file.size <= 0) {
      setPending(null);
      setStage({
        kind: "failed",
        failure: {
          message: "空のファイルはアップロードできません。",
          code: "INVALID_FILE_SIZE",
          reloadHelps: false,
          fullPageReloadRequired: false,
        },
      });
      return;
    }
    setPending({ file, sha256: null, idempotencyKey: newIdempotencyKey() });
    setStage({ kind: "ready" });
  };

  const upload = async () => {
    if (pending === null || usingFixtures || running) return;
    const abortController = new AbortController();
    controller.current = abortController;
    try {
      let digest = pending.sha256;
      if (digest === null) {
        setStage({ kind: "hashing", processed: 0, total: pending.file.size });
        digest = await sha256Blob(pending.file, {
          signal: abortController.signal,
          onProgress: (processed, total) => {
            setStage({ kind: "hashing", processed, total });
          },
        });
        setPending((current) => current === null ? null : { ...current, sha256: digest });
      }

      setStage({ kind: "requesting" });
      const intent = await api.createMediaUploadIntent(
        {
          originalName: pending.file.name,
          contentType: "video/mp4",
          byteSize: pending.file.size,
          sha256: digest,
        },
        pending.idempotencyKey,
      );

      setStage({ kind: "uploading", processed: 0, total: pending.file.size });
      await putPresignedUpload(intent.upload, pending.file, {
        signal: abortController.signal,
        onProgress: (processed, total) => {
          setStage({ kind: "uploading", processed, total });
        },
      });

      setStage({ kind: "completing" });
      const media = await api.completeMediaUpload(intent.media.id, {
        byteSize: pending.file.size,
        sha256: digest,
      });
      setStage({ kind: "success", media });
      onUploaded(media);
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === "AbortError") {
        setStage({ kind: "cancelled" });
      } else {
        const failure = describeFailure(error);
        setStage({ kind: "failed", failure });
      }
    } finally {
      controller.current = null;
    }
  };

  const label = stageLabel(stage);

  return (
    <div className="modal-backdrop" role="presentation">
      <section className="upload-dialog" role="dialog" aria-modal="true" aria-labelledby="upload-dialog-title" aria-describedby="upload-dialog-description">
        <div className="upload-dialog__head">
          <div>
            <p className="eyebrow eyebrow--blue">MEDIA UPLOAD</p>
            <h2 id="upload-dialog-title">動画をアップロード</h2>
          </div>
          <button ref={closeButton} type="button" aria-label="閉じる" disabled={running} onClick={onClose}>×</button>
        </div>

        <p className="upload-dialog__lead" id="upload-dialog-description">送信前にSHA-256を計算し、送信後にサーバー側で完全性と動画形式を検査します。</p>

        {usingFixtures && <div className="inline-alert"><b>デモモードでは送信できません</b><span><code>VITE_API_BASE_URL</code> を設定した実API接続環境で実行してください。</span></div>}

        <label className={`upload-dropzone ${pending === null ? "" : "upload-dropzone--selected"}`}>
          <span className="upload-dropzone__icon">↑</span>
          <b>{pending === null ? "MP4動画を選択" : pending.file.name}</b>
          <small>{pending === null ? "MP4 / H.264 / AAC（音声なし可）" : byteLabel(pending.file.size)}</small>
          <input type="file" accept="video/mp4,.mp4" disabled={running} onChange={choose} />
        </label>

        {label !== null && <div className="upload-progress" aria-live="polite">
          <span>{label}</span>
          {(stage.kind === "hashing" || stage.kind === "uploading") && <progress value={stage.processed} max={stage.total} />}
        </div>}

        {stage.kind === "failed" && <div className="inline-alert"><b>アップロードできませんでした</b><span>{stage.failure.message}{stage.failure.code === null ? "" : `（${stage.failure.code}）`}</span><AuthenticationRecoveryButton failure={stage.failure} /></div>}
        {stage.kind === "cancelled" && <p className="resource-note">操作を取り消しました。同じファイルでもう一度実行できます。</p>}
        {stage.kind === "success" && <div className="upload-success" aria-live="polite"><b>アップロードを受け付けました</b><span>形式検査を開始しています。完了まで公開はできません。</span></div>}

        <div className="upload-dialog__actions">
          {running
            ? <button className="secondary-button" type="button" onClick={() => { controller.current?.abort(); }}>取り消す</button>
            : <button className="secondary-button" type="button" onClick={onClose}>{stage.kind === "success" ? "閉じる" : "やめる"}</button>}
          {stage.kind !== "success" && <button className="admin-primary" type="button" disabled={pending === null || usingFixtures || running} onClick={() => { void upload(); }}>
            {stage.kind === "failed" || stage.kind === "cancelled" ? "再試行する" : "完全性を確認して送信"}
          </button>}
        </div>
      </section>
    </div>
  );
}
